import React, { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Resources';
import block from 'propmods';

import {
    deleteSkillImage,
    deleteSkillSound,
    getResourcesStatus,
    getSkillImages,
    getSkillSounds,
    uploadSkillImage,
    uploadSkillSound,
} from '../../api';
import { routes } from '../../lib/routes';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import Menu from '../Menu/Menu';

import ImageTable from './ImageTable';
import ResourceManager from './ResourceManager';
import SoundTable from './SoundTable';

import './ResourcesPage.scss';

const b = block('ResourcesPage');

const t = i18n(keyset);

interface Props {
    skill: {
        id: string;
    };
    disabled: boolean;
}

const ResourcesPage: FC<Props> = ({ skill, disabled }) => {
    const allowUploadSounds = Boolean(__appConfig.user.featureFlags.allowUploadSounds);

    const menuItems = [
        {
            path: routes.app.skillResources('images', skill.id),
            title: t('menu-images'),
        },
    ];

    if (allowUploadSounds) {
        menuItems.push({
            path: routes.app.skillResources('sounds', skill.id),
            title: t('menu-sounds'),
        });
    }

    return (
        <ErrorBoundary withHomepageLink>
            <div {...b()}>
                <Menu items={menuItems} />
                <Switch>
                    <Route
                        path={routes.app.skillResources('images')}
                        render={() => (
                            <ResourceManager
                                key={`${skill.id}__images`}
                                type="images"
                                skill={skill}
                                getList={(page: number) => getSkillImages(skill.id, page)}
                                upload={(f) => uploadSkillImage(skill.id, f)}
                                remove={(imageId: string) => deleteSkillImage(skill.id, imageId)}
                                getQuota={() => getResourcesStatus().then((x) => x.images)}
                                accept="image/*"
                                uploadText={<p>{t('info-to-upload')}</p>}
                                disabled={disabled}
                            >
                                {({ items, remove }) => (
                                    <ImageTable items={items} remove={remove} disabled={disabled} />
                                )}
                            </ResourceManager>
                        )}
                    />
                    {allowUploadSounds && (
                        <Route
                            path={routes.app.skillResources('sounds')}
                            render={() => (
                                <ResourceManager
                                    key={`${skill.id}__sounds`}
                                    type="sounds"
                                    skill={skill}
                                    getList={(page: number) => getSkillSounds(skill.id, page)}
                                    upload={(f) => uploadSkillSound(skill.id, f)}
                                    remove={(soundId: string) => deleteSkillSound(skill.id, soundId)}
                                    getQuota={() => getResourcesStatus().then((x) => x.sounds)}
                                    accept="audio/*"
                                    uploadText={
                                        <p>
                                            <div>{t('info-to-upload')}</div>
                                            <br />
                                            <div>{t('info-sounds')}</div>
                                        </p>
                                    }
                                    disabled={disabled}
                                >
                                    {({ items, remove }) => (
                                        <SoundTable
                                            items={items}
                                            remove={remove}
                                            skillId={skill.id}
                                            disabled={disabled}
                                        />
                                    )}
                                </ResourceManager>
                            )}
                        />
                    )}

                    <Redirect
                        exact
                        from={routes.app.skillResources('', skill.id)}
                        to={routes.app.skillResources('images', skill.id)}
                    />
                </Switch>
            </div>
        </ErrorBoundary>
    );
};

export default ResourcesPage;
